import { CreateFocusCss } from '../../../styles'
import type { Tokens } from '../../../theming/types'
import { styles } from '../../../utilities'
import type { RadioButtonProps } from '..'

export default (config: {
  props: Pick<RadioButtonProps, 'disabled' | 'hideLabel'> & {
    isInvalid?: boolean
  }
  tokens: Tokens
}) => {
  const { props, tokens } = config

  return {
    default: {
      wrap: {
        position: 'relative',
      },

      innerWrap: {
        display: 'flex',
        position: 'relative',
      },

      input: {
        appearance: 'none',
        borderRadius: '50%',
        borderWidth: 0,
        boxShadow: 'none',
        cursor: 'pointer',
        flexShrink: 0,
        margin: 0,
        outline: 'none',
        padding: 0,
        position: 'relative',

        ...CreateFocusCss({
          ...tokens.focusOuter,
          borderRadius: 0,
          outlineOffset: tokens.focusOutsetM,
        }),

        '&:disabled': {
          cursor: 'not-allowed',
        },
      },

      label: {
        alignItems: 'flex-start',
        color: tokens.fontColorDefault,
        display: 'inline-flex',
        opacity: props.disabled ? tokens.opacityDisabled : undefined,

        '&:hover': {
          cursor: props.disabled ? 'not-allowed' : 'pointer',
        },
      },

      labelText: {
        ...(props.hideLabel && styles.visuallyHidden),
      },

      description: {
        marginInlineEnd: 0,
        marginBlockEnd: 0,
      },

      error: {
        marginInlineEnd: 0,
        marginBlockEnd: 0,
      },

      focusStyles: {
        pointerEvents: 'none',
        position: 'absolute',
      },

      radioButtonHover: {
        cursor: props.disabled ? 'not-allowed' : undefined,
      },

      radioButtonBorder: {
        backgroundColor: tokens.formBgColorDefault,
        borderColor: props.isInvalid
          ? `${tokens.formBorderColorError} !important`
          : tokens.formBorderColorDefault,
        borderRadius: '50%',
        borderStyle: 'solid',
        content: '""',
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        flex: '0 0 auto',
        opacity: props.disabled ? tokens.opacityDisabled : undefined,
        position: 'relative',
      },

      radioButtonFill: {
        alignSelf: 'center',
        borderRadius: '50%',
        borderColor: props.isInvalid
          ? `${tokens.formBgColorError} !important`
          : tokens.formBgColorDefault,
        content: '""',
        cursor: props.disabled ? 'not-allowed' : undefined,
        display: 'block',
        pointerEvents: 'none',
      },
    },

    motionVariants: {
      radioButtonBorder: {
        checked: {
          borderColor: tokens.formBorderColorSelected,
          transition: { duration: 0.15 },
        },
        unchecked: {
          borderColor: tokens.formBorderColorDefault,
          transition: { duration: 0.15 },
        },
        hoverWhenChecked: {
          borderColor: tokens.formBorderColorSelectedHover,
          transition: { duration: 0.1 },
        },
        hoverWhenUnchecked: {
          borderColor: tokens.formBorderColorHover,
          transition: { duration: 0.1 },
        },
        tapWhenChecked: {
          borderColor: tokens.formBorderColorSelectedActive,
          transition: { duration: 0.15 },
        },
        tapWhenUnchecked: {
          borderColor: tokens.formBorderColorActive,
          transition: { duration: 0.15 },
        },
      },
      radioButtonFill: {
        checked: {
          opacity: 1,
          scale: 1,
          transition: { duration: 0.15 },
        },
        unchecked: {
          opacity: 0,
          scale: 0,
          transition: { duration: 0.15 },
        },
        hoverWhenChecked: {
          borderColor: tokens.formBgColorSelectedHover,
          transition: { duration: 0.15 },
        },
        tapWhenChecked: {
          borderColor: tokens.formBgColorSelectedActive,
          transition: { duration: 0.15 },
        },
      },
    },
  } as const
}
